/**
 * @file useUserProfile.js
 * @feature Auth
 * @description Fetches and caches the user profile from the API.
 * Handles 401 token expiry, stores profile in sessionStorage, and
 * triggers EULA modal when needed.
 *
 * FDD rationale: "fetch & cache user profile" is a single named feature
 * behaviour; components should never call fetch directly for profile data.
 */

import { useCallback, useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { UserContext } from '../../components/UserContext';
import { post_api_headers } from '../../utils/Store';
import { AUTH_SCOPES } from '../../config/Auth.config';

// ✅ Module-level constant — not a reactive value, no need in dependency array
const BASE_URL = process.env.REACT_APP_API_USER_PROFILE;

/**
 * @param {Function} onEulaRequired  – called with userId when EULA hasn't been accepted
 */
export const useUserProfile = (onEulaRequired) => {
  const { instance } = useMsal();
  const { setUserProfile, setLoading } = useContext(UserContext);
  const navigate = useNavigate();
  const post_headers = useAtom(post_api_headers)[0];

  // ✅ Inner helper: performs a single fetch attempt with a given token.
  // Extracted so the 401-retry path can call it directly without a
  // forward-reference cycle on fetchUserProfile itself.
  const fetchOnce = useCallback(
    async (idToken) => {
      const response = await fetch(`${BASE_URL}/api/users/user-profile`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}`, ...post_headers },
        body: JSON.stringify({ token: idToken }),
        cache: 'no-store',
      });
      return response;
    },
    [post_headers],
  );

  const fetchUserProfile = useCallback(
    async (token) => {
      const idToken = token ?? sessionStorage.getItem('id_token');
      if (!idToken) {
        setLoading(false);
        return null;
      }

      try {
        let response = await fetchOnce(idToken);

        // ✅ Token expired — silently refresh once, then retry with fetchOnce
        // (avoids recursive reference to fetchUserProfile before it is declared)
        if (response.status === 401) {
          const account = instance.getActiveAccount();
          if (account) {
            const refreshed = await instance.acquireTokenSilent({
              scopes: AUTH_SCOPES,
              account,
              forceRefresh: true,
            });
            if (refreshed.idToken) {
              sessionStorage.setItem('id_token', refreshed.idToken);
              // ✅ Retry using fetchOnce — no forward-reference cycle
              response = await fetchOnce(refreshed.idToken);
            }
          }

          // If still unauthorized after refresh attempt, redirect
          if (response.status === 401) {
            navigate('/unauthuser');
            return null;
          }
        }

        if (!response.ok) throw new Error(`API error: ${response.status}`);

        const data = await response.json();
        sessionStorage.setItem('userProfile', JSON.stringify(data));
        setUserProfile(data);
        setLoading(false);

        if (!data.eulaAccepted && onEulaRequired) {
          onEulaRequired(data.userId);
        }

        return data;
      } catch {
        setLoading(false);
        return null;
      }
    },
    // ✅ BASE_URL removed — module-level constant, never changes
    [fetchOnce, instance, navigate, setLoading, setUserProfile, onEulaRequired],
  );

  return { fetchUserProfile };
};